import { useQuery, QueryConfig, MutateConfig, useMutation, useInfiniteQuery, InfiniteQueryConfig } from "react-query";
import { buildServerRollupCacheKeys, IReadSnapshotFilters, buildSearchHashtagsCacheKeys } from "./utils";
import { serverRollup } from "../services/server-rollups";
import { ICreateRollupSnapshotRequest, IRollupSnapshotResponse } from "../types/server-rollups";
import { IServerRollupResponse } from "helpers/server-rollup";
import { IServerRollupRequest } from "helpers/query-dsl-types";
import { buildFetchHashtagsQuery, buildFilterProductByIdQuery } from "helpers/query-dsl-builder";

export const useCreateRollupSnapshot = (mutationObject?: MutateConfig<IRollupSnapshotResponse, Error>) => {
    return useMutation<IRollupSnapshotResponse, Error, ICreateRollupSnapshotRequest>(serverRollup.createSnapshot, {
        ...mutationObject,
        onSuccess: (data: IRollupSnapshotResponse, variables: ICreateRollupSnapshotRequest) => {
            mutationObject?.onSuccess?.(data, variables);
        },
        onError: (error: Error, variables: ICreateRollupSnapshotRequest, snap) => {
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useLastRollupSnapshot = (
    itemId: string,
    merchantId: string,
    options?: QueryConfig<nuorder.folder.v1.Item>
) => {
    return useQuery<nuorder.folder.v1.Item>(
        buildServerRollupCacheKeys(merchantId, itemId),
        () => serverRollup.getLastSnapshot(itemId, merchantId),
        options
    );
};

export const useFetchSnapshotStatus = (
    snapshotId: string,
    merchantId: string,
    options?: QueryConfig<IRollupSnapshotResponse>
) => {
    return useQuery<IRollupSnapshotResponse, any>(
        buildServerRollupCacheKeys(merchantId, null, snapshotId),
        () => serverRollup.getSnapshot(snapshotId, merchantId),
        options
    );
};

export const useFetchCollectionIdsFromSnapshot = (
    snapshotId: string,
    merchantId: string,
    options?: QueryConfig<string[]>
) => {
    return useQuery<string[]>(
        buildServerRollupCacheKeys(merchantId, null, snapshotId, { withSnapshot: true }),
        () =>
            serverRollup
                .getSnapshot(snapshotId, merchantId, true)
                .then((data) => data?.collection_refs?.map((it) => it.collection_id)) as any,
        options
    );
};

export const useReadSnapshotReview = (
    snapshotId: string,
    merchantId: string,
    filters: IReadSnapshotFilters,
    options?: QueryConfig<IServerRollupResponse>
) => {
    return useQuery<IServerRollupResponse>(
        buildServerRollupCacheKeys(merchantId, null, snapshotId, { readSnapshot: true, filters }),
        () => serverRollup.readSnapshotReview(snapshotId, merchantId, filters),
        options
    );
};

export const useFetchSnapshotItemById = (
    snapshotId: string,
    merchantId: string,
    productId: string,
    collectionId: string,
    options?: QueryConfig<IServerRollupResponse>
) => {
    const filters = buildFilterProductByIdQuery(merchantId, productId, collectionId);
    return useQuery<IServerRollupResponse>(
        buildServerRollupCacheKeys(filters.merchant_id, null, snapshotId, {
            readSnapshot: true,
            readSnapshotItems: true,
            filters,
        }),
        () => serverRollup.fetchSnapshotItems(snapshotId, filters),
        options
    );
};

export const useFetchRollupHashtags = (
    snapshotId: string,
    merchantId: string,
    hashtagSearchText: string,
    options?: QueryConfig<string[]>
) => {
    const query = buildFetchHashtagsQuery(merchantId, hashtagSearchText);

    return useQuery<string[]>(
        buildSearchHashtagsCacheKeys(snapshotId, merchantId, hashtagSearchText),
        () =>
            serverRollup.fetchSnapshotItems(snapshotId, query).then(({ items }) => {
                return items.map((item: any) => item.aggregation?.group_key?.string_value.replace("#", ""));
            }),
        options
    );
};

export const useInfiniteFetchSnapshotItems = (
    snapshotId: string,
    filters: IServerRollupRequest,
    options?: Partial<InfiniteQueryConfig<IServerRollupResponse, string[]>>
) => {
    return useInfiniteQuery<IServerRollupResponse, string[]>(
        buildServerRollupCacheKeys(filters.merchant_id, null, snapshotId, { readSnapshotItems: true, filters }),
        (_pageKey, _filterKey, page_token: string[]) => {
            return serverRollup.fetchSnapshotItems(snapshotId, {
                ...filters,
                ...(page_token?.length && { search_after: page_token }),
            });
        },
        {
            ...options,
            getFetchMore: (lastResponse: IServerRollupResponse) => {
                return lastResponse.items?.length >= filters.page_size && lastResponse.search_after?.length
                    ? lastResponse.search_after
                    : null;
            },
        }
    );
};
